import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Crown, Lock, Zap, Users } from 'lucide-react';
import { subscriptionService } from '@/utils/subscriptionService';
import { MODULE_ACCESS, MODULE_DISPLAY_NAMES } from '@/constants/moduleAccess';
import { UpgradeModal } from '@/components/UpgradeModal';
import { ModuleAccessSkeleton } from '@/components/ui/loading-states';

interface ModuleAccessGateProps {
  moduleName: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  showUpgradePrompt?: boolean;
}

export const ModuleAccessGate: React.FC<ModuleAccessGateProps> = ({
  moduleName,
  children,
  fallback,
  showUpgradePrompt = true
}) => {
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [currentPlan, setCurrentPlan] = useState<string>('');
  const [requiredPlan, setRequiredPlan] = useState<string>('');
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    checkAccess();
  }, [moduleName]);

  const resolvedModuleName = MODULE_ACCESS[moduleName as keyof typeof MODULE_ACCESS] || moduleName.toLowerCase();

  const checkAccess = async () => {
    try {
      setLoading(true);
      
      // Parallel calls for better performance
      const [access, plan, superAdmin, recommendation] = await Promise.all([
        subscriptionService.hasModuleAccess(resolvedModuleName),
        subscriptionService.getPlanName(),
        subscriptionService.isUserSuperAdmin(),
        subscriptionService.getUpgradeRecommendation(resolvedModuleName)
      ]);
      
      setHasAccess(access);
      setCurrentPlan(plan);
      setRequiredPlan(access ? plan : recommendation);
      setIsSuperAdmin(superAdmin);
      
    } catch (error) {
      console.error('Error checking module access:', error);
      setHasAccess(false);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = () => {
    setShowUpgradeModal(true);
  };

  const getModuleIcon = (module: string) => {
    switch (module) {
      case 'climate_risk_assessment':
        return <Crown className="h-6 w-6" />;
      case 'double_materiality_assessment':
        return <Lock className="h-6 w-6" />;
      case 'organization_team_management':
        return <Users className="h-6 w-6" />;
      default:
        return <Zap className="h-6 w-6" />;
    }
  };

  const getPlanIcon = (plan: string) => {
    switch (plan.toLowerCase()) {
      case 'individual':
        return <Zap className="h-5 w-5" />;
      case 'corporate':
        return <Users className="h-5 w-5" />;
      case 'enterprise':
        return <Crown className="h-5 w-5" />;
      default:
        return <Zap className="h-5 w-5" />;
    }
  };

  if (loading) {
    return <ModuleAccessSkeleton />;
  }

  // SuperAdmin has access to everything
  if (isSuperAdmin) {
    return <>{children}</>;
  }

  // User has access
  if (hasAccess) {
    return <>{children}</>;
  }

  // Custom fallback
  if (fallback) {
    return <>{fallback}</>;
  }

  // Default upgrade prompt
  if (showUpgradePrompt) {
    const displayName = MODULE_DISPLAY_NAMES[resolvedModuleName as keyof typeof MODULE_DISPLAY_NAMES] || 
                       resolvedModuleName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

    return (
      <>
        <Card className="border-2 border-dashed border-gray-300">
          <CardHeader className="text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
              {getModuleIcon(resolvedModuleName)}
              <CardTitle className="text-xl">{displayName}</CardTitle>
            </div>
            <CardDescription>
              This feature requires a higher subscription plan
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-2">
                  {getPlanIcon(currentPlan)}
                  <span className="font-medium">Current Plan: {currentPlan}</span>
                </div>
                <Badge variant="secondary">Limited Access</Badge>
              </div>

              <div className="flex items-center justify-between p-3 border-2 border-blue-500 rounded-lg bg-blue-50">
                <div className="flex items-center gap-2">
                  {getPlanIcon(requiredPlan)}
                  <span className="font-medium">Required: {requiredPlan}</span>
                </div>
                <Badge className="bg-blue-500">Full Access</Badge>
              </div>
            </div>

            <Button onClick={handleUpgrade} className="w-full">
              Upgrade to {requiredPlan}
            </Button>
          </CardContent>
        </Card>

        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          moduleName={resolvedModuleName}
          currentPlan={currentPlan}
          requiredPlan={requiredPlan}
        />
      </>
    );
  }

  // No access, no prompt
  return null;
}; 
